import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiDownload, ApiError } from '../api/client';
import { businessBySlug } from '../domain/businesses';
import { formatMkd } from '../lib/format';
import { btn } from '../components/FilterBar';
import { useToast } from '../components/Toast';

/**
 * Devijimi — planned versus actual, per category and month.
 * Ports frmShpenzimetDevijimi{Stargym,Starbar,Missfit,Stargroup}.
 *
 * Positive deviation means under budget, which the desktop showed in green
 * with a leading "+". Same convention here.
 */

interface DevijimiRow {
  categoryId: number;
  category: string;
  planned: number;
  actual: number;
  deviation: number;
}

interface DevijimiMonth {
  year: number;
  month: number;
  monthName: string;
  rows: DevijimiRow[];
  totalPlanned: number;
  totalActual: number;
  totalDeviation: number;
}

interface Response {
  summary: { actual: number; planned: number; deviation: number };
  months: DevijimiMonth[];
}

/** Signed, with the desktop's leading "+" for a positive (under-budget) value. */
function formatDeviation(value: number): string {
  const formatted = formatMkd(Math.abs(value));
  return value >= 0 ? `+${formatted}` : `-${formatted}`;
}

function deviationClass(value: number): string {
  return value >= 0 ? 'text-emerald-700' : 'text-red-600';
}

export default function ShpenzimetDevijimi() {
  const { biznesi = '' } = useParams();
  const business = businessBySlug(biznesi);
  const toast = useToast();

  const devijimiQuery = useQuery({
    queryKey: ['devijimi', biznesi],
    queryFn: () => apiGet<Response>(`/shpenzimet/devijimi/${biznesi}`),
    enabled: Boolean(business),
  });

  async function handleExport() {
    try {
      await apiDownload(`/shpenzimet/devijimi/${biznesi}/eksport`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Eksportimi dështoi.');
    }
  }

  if (!business) {
    return <div className="rounded-xl bg-white p-6 text-red-700">Biznesi nuk u gjet.</div>;
  }

  const summary = devijimiQuery.data?.summary;
  const months = devijimiQuery.data?.months ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Devijimi i shpenzimeve — {business.name}</h1>
          <p className="text-sm text-muted">
            Planifikuar kundrejt aktuales. Vlera pozitive do të thotë nën buxhet.
          </p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={months.length === 0}
          className={btn.secondary}
        >
          Eksporto Excel
        </button>
      </div>

      {/* Current-month headline figures */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl bg-white p-4">
          <div className="text-xs font-medium text-muted">Shpenzimet aktuale (ky muaj)</div>
          <div className="tnum mt-1 text-xl font-semibold">{formatMkd(summary?.actual ?? 0)}</div>
        </div>
        <div className="rounded-xl bg-white p-4">
          <div className="text-xs font-medium text-muted">Të planifikuara (ky muaj)</div>
          <div className="tnum mt-1 text-xl font-semibold">{formatMkd(summary?.planned ?? 0)}</div>
        </div>
        <div className="rounded-xl bg-white p-4">
          <div className="text-xs font-medium text-muted">Devijimi</div>
          <div
            className={`tnum mt-1 text-xl font-semibold ${deviationClass(summary?.deviation ?? 0)}`}
          >
            {formatDeviation(summary?.deviation ?? 0)}
          </div>
        </div>
      </div>

      {devijimiQuery.isLoading && (
        <div className="rounded-xl bg-white p-10 text-center text-muted">Duke ngarkuar…</div>
      )}

      {!devijimiQuery.isLoading && months.length === 0 && (
        <div className="rounded-xl bg-white p-10 text-center text-muted">
          Nuk ka të dhëna për këtë biznes.
        </div>
      )}

      {months.map((m) => (
        <div key={`${m.year}-${m.month}`} className="overflow-hidden rounded-xl bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <h2 className="font-semibold">
              {m.monthName} {m.year}
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                  <th className="px-4 py-2.5 font-semibold">Kategoria</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Planifikuar</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Aktuale</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Devijimi</th>
                </tr>
              </thead>
              <tbody>
                {m.rows.map((r) => (
                  <tr key={r.categoryId} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2">{r.category}</td>
                    <td className="tnum px-4 py-2 text-right">{formatMkd(r.planned)}</td>
                    <td className="tnum px-4 py-2 text-right">{formatMkd(r.actual)}</td>
                    <td className={`tnum px-4 py-2 text-right font-medium ${deviationClass(r.deviation)}`}>
                      {formatDeviation(r.deviation)}
                    </td>
                  </tr>
                ))}

                <tr className="border-t-2 border-slate-300 bg-slate-100 font-semibold">
                  <td className="px-4 py-2.5">TOTAL</td>
                  <td className="tnum px-4 py-2.5 text-right">{formatMkd(m.totalPlanned)}</td>
                  <td className="tnum px-4 py-2.5 text-right">{formatMkd(m.totalActual)}</td>
                  <td className={`tnum px-4 py-2.5 text-right ${deviationClass(m.totalDeviation)}`}>
                    {formatDeviation(m.totalDeviation)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
